import { Close } from "@mui/icons-material";
import {
  Button,
  IconButton,
  Modal,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUploadFileMutation } from "src/api/services/bucketApi";
import { FileType } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import ImageUploader from "src/components/ImageUploader";
import { useIsMobile } from "src/hooks/useIsMobile";
import ErrorText from "src/shared/components/ErrorText";
import { convertToBlob } from "src/utils/convertToBlob";
import { desktopSx, mobileSx } from "src/utils/styles/modalStyles";

const tabs = ["Front", "Back"];

const mapTabToFile: Record<number, string> = {
  0: "front-of-card",
  1: "back-of-card",
};

interface Props {
  open: boolean;
  onClose: () => void;
}

const UpdateInsuranceModal = ({ onClose, open }: Props) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [uploadFile] = useUploadFileMutation();
  const component = "insurance-details.add-new-insurance";
  const [file, setFile] = useState<FileType | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState<number>(0);

  const fileName = mapTabToFile[tabValue];

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: number) => {
      setTabValue(newValue);
      setFile(null);
    },
    [],
  );

  const handleImage = useCallback(
    (image: FileType | null) => {
      setFile(image);
      setError("");
    },
    [setFile],
  );

  const deleteFile = useCallback(() => {
    handleImage(null);
  }, [handleImage]);

  const onConfirm = useCallback(async () => {
    if (!file) {
      setError("Please select the image you want to upload");
      return;
    }
    setLoading(true);

    const blob = await convertToBlob(file.fileToUpload);

    return uploadFile({
      file: blob,
      name: fileName,
      type: blob.type,
      folder: "insurance",
    })
      .then(() => {
        onClose();
        setFile(null);
        setTabValue(0);
      })
      .catch((err) => {
        setError((err as Error).message);
      })
      .finally(() => setLoading(false));
  }, [file, fileName, onClose, uploadFile]);

  return (
    <Modal open={open}>
      <Stack sx={isMobile ? mobileSx : desktopSx} gap={1}>
        <Stack direction="row" justifyContent="space-between">
          <Stack>
            <Typography variant="h6" fontWeight={600}>
              {t(`${component}.header`, "Insurance Details")}
            </Typography>
            <Typography variant="subtitle1">
              {t(`${component}.subheader`, "Upload Insurance Card")}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} sx={{ alignSelf: "baseline" }}>
            <Close />
          </IconButton>
        </Stack>
        <Stack direction={"row"}>
          <Tabs value={tabValue} onChange={handleChange}>
            {tabs.map((tab) => (
              <Tab key={tab} label={tab} />
            ))}
          </Tabs>
        </Stack>
        <ImageUploader
          folder="insurance"
          file={file}
          name={fileName}
          setFilePath={handleImage}
          subtitle={null}
          title={null}
        />
        {error ? <ErrorText message={error} /> : null}
        {file ? (
          <Stack
            mt={3}
            alignItems="center"
            gap={1}
            direction={"row"}
            justifyContent={isMobile ? "center" : "end"}
          >
            <Button
              variant="text"
              onClick={deleteFile}
              fullWidth={isMobile ? true : true}
              sx={{
                width: isMobile ? "100%" : "20%",
                color: "#000000",
                fontWeight: 700,
                fontSize: "1rem",
              }}
            >
              {t(`button.cancel`)}
            </Button>
            <LoadingButton
              loading={loading}
              onClick={onConfirm}
              fullWidth={isMobile ? true : false}
              sx={{ width: isMobile ? "100%" : "30%" }}
            >
              {t(`button.confirm`, "Confirm")}
            </LoadingButton>
          </Stack>
        ) : null}
      </Stack>
    </Modal>
  );
};

export default UpdateInsuranceModal;
